/**
 * City Generator — Orchestrator
 * Routes generation to district-based sub-generators for diverse urban layouts.
 * Each district type produces a visually distinct region: grid streets (residential),
 * open plazas (market), wide estates (noble), or organic alleys (slums).
 *
 * Flow:
 * 1. Determine district types (from config or auto-select)
 * 2. Partition map into district rectangles via BSP
 * 3. Carve connecting avenues along split lines
 * 4. Call each district's sub-generator
 * 5. Stitch boundaries
 * 6. Assign building categories (district-biased)
 * 7. Populate buildings with NPCs/furniture
 * 8. Optional canal, player placement
 * 9. Attach MapMetadata with district info
 */

import type { GridMap, GeneratorConfig, CityDistrictType, DistrictMetadata } from '../types';
import { createGrid, setTerrain, addEntity, getCell, inBounds } from '../grid';
import { SeededRandom } from '../random';
import { assignCategories, populateBuildings, type CityBuilding } from './city-catalog';
import {
  partitionIntoDistricts,
  collectLeaves,
  carveAvenues,
  stitchBoundaries,
  generateResidential,
  generateMarket,
  generateNoble,
  generateSlums,
  type DistrictRect,
} from './city-districts';

// ============================================================================
// DEFAULT DISTRICT POOL (auto-select uses weighted random from these)
// ============================================================================

const DEFAULT_DISTRICT_POOL: Array<{ type: CityDistrictType; weight: number }> = [
  { type: 'residential', weight: 4 },
  { type: 'market', weight: 2 },
  { type: 'noble', weight: 2 },
  { type: 'slums', weight: 2 },
];

// ============================================================================
// MAIN GENERATOR
// ============================================================================

export function generateCity(config: GeneratorConfig): GridMap {
  const { width, height, seed } = config;
  const hasCanal = config.hasCanal ?? false;
  const rng = new SeededRandom(seed);

  let map = createGrid(width, height, 'wall');
  map.seed = seed;
  map.name = 'City';

  // Step 1: Determine district types
  const districtTypes = resolveDistrictTypes(config, rng);

  // Step 2: Partition map into district rectangles via BSP
  const avenueWidth = 2;
  const bspRoot = partitionIntoDistricts(0, 0, width, height, districtTypes.length, rng);
  const leafRects = collectLeaves(bspRoot);

  // Assign district types to leaf rectangles
  const districts: DistrictRect[] = leafRects.map((leaf, i) => ({
    ...leaf,
    type: districtTypes[i % districtTypes.length],
    id: i,
  }));

  // Step 3: Carve connecting avenues along BSP split lines
  carveAvenues(map, bspRoot, avenueWidth);

  // Step 4: Call each district's sub-generator
  let roomIdCounter = 0;
  const allBuildings: CityBuilding[] = [];
  const districtMetadata: DistrictMetadata[] = [];

  for (const district of districts) {
    const result = generateDistrict(map, district, rng, roomIdCounter);
    roomIdCounter += result.rooms.length + 100; // gap to avoid ID collisions
    allBuildings.push(...result.buildings);

    districtMetadata.push({
      id: district.id,
      type: district.type,
      bounds: { x: district.x, y: district.y, width: district.width, height: district.height },
      buildingCount: result.buildings.length,
    });
  }

  // Step 5: Stitch boundaries between districts
  stitchBoundaries(map, districts, avenueWidth);

  // Step 6: Assign building categories with district bias
  for (const district of districts) {
    const districtBuildings = allBuildings.filter(b => b.districtType === district.type);
    const cx = district.x + Math.floor(district.width / 2);
    const cy = district.y + Math.floor(district.height / 2);
    assignCategories(districtBuildings, cx, cy, rng, district.type);
  }

  // Step 7: Populate buildings with NPCs and furniture
  map = populateBuildings(map, allBuildings, rng);

  // Step 8: Optional canal
  if (hasCanal && width > 40) {
    carveCanal(map, width, height, rng);
  }

  // Step 9: Place player on a floor tile near center
  map = placePlayer(map, width, height, rng);

  // Step 10: Attach metadata
  map.metadata = { districts: districtMetadata };

  return map;
}

// ============================================================================
// DISTRICT TYPE RESOLUTION
// ============================================================================

function resolveDistrictTypes(config: GeneratorConfig, rng: SeededRandom): CityDistrictType[] {
  if (config.districts && config.districts.length > 0) {
    return config.districts.map(d => d.type);
  }

  // Auto-select 3-5 districts based on map size
  const area = config.width * config.height;
  const count = area >= 4000 ? rng.int(3, 5) : area >= 2000 ? rng.int(2, 4) : rng.int(2, 3);

  // Weighted random selection from pool
  const types: CityDistrictType[] = [];
  const pool = [...DEFAULT_DISTRICT_POOL];

  // Always include at least one residential
  types.push('residential');

  while (types.length < count) {
    const totalWeight = pool.reduce((sum, p) => sum + p.weight, 0);
    let roll = rng.range(0, totalWeight);
    for (const entry of pool) {
      roll -= entry.weight;
      if (roll <= 0) {
        types.push(entry.type);
        break;
      }
    }
  }

  return rng.shuffle(types);
}

// ============================================================================
// DISTRICT DISPATCH
// ============================================================================

function generateDistrict(
  map: GridMap, district: DistrictRect, rng: SeededRandom, roomIdStart: number,
) {
  switch (district.type) {
    case 'market':
      return generateMarket(map, district, rng, roomIdStart);
    case 'noble':
      return generateNoble(map, district, rng, roomIdStart);
    case 'slums':
      return generateSlums(map, district, rng, roomIdStart);
    case 'residential':
    default:
      return generateResidential(map, district, rng, roomIdStart);
  }
}

// ============================================================================
// CANAL (optional)
// ============================================================================

function carveCanal(map: GridMap, width: number, height: number, rng: SeededRandom): void {
  const cx = Math.floor(width / 2) + (rng.bool() ? -6 : 6);
  if (cx < 2 || cx >= width - 2) return;

  for (let y = 1; y < height - 1; y++) {
    const cell = getCell(map, cx, y);
    if (!cell) continue;
    if (cell.terrain === 'floor') {
      setTerrain(map, cx, y, 'water');
    }
  }

  // Bridge crossings every ~10 tiles
  for (let y = 5; y < height - 5; y += 8 + rng.int(0, 4)) {
    if (inBounds(map, cx, y)) setTerrain(map, cx, y, 'floor');
  }
}

// ============================================================================
// PLAYER PLACEMENT
// ============================================================================

function placePlayer(map: GridMap, width: number, height: number, _rng: SeededRandom): GridMap {
  // Try center first, then spiral outward to find a floor tile
  const cx = Math.floor(width / 2);
  const cy = Math.floor(height / 2);

  for (let r = 0; r < Math.max(width, height); r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue; // only check perimeter
        const x = cx + dx;
        const y = cy + dy;
        const cell = getCell(map, x, y);
        if (cell && cell.terrain === 'floor') {
          return addEntity(map, {
            id: 'player',
            x, y,
            type: 'player',
            name: 'Player',
            lightRadius: 12,
          });
        }
      }
    }
  }

  // Fallback: just place at center
  return addEntity(map, {
    id: 'player',
    x: cx, y: cy,
    type: 'player',
    name: 'Player',
    lightRadius: 12,
  });
}
